import { ComponentFactory, ComponentFactoryResolver, ComponentRef } from '@angular/core';
import { ChatAdapter, Message, User, UserStatus, Window } from 'ng-chat';
import { ChatMenu } from 'ng-chat/ng-chat/core/chat-menu';
import { Observable, ReplaySubject } from "rxjs/Rx";
import { UscChatDetailsComponent } from "../usc-chat-details/usc-chat-details.component";
import { RECORD_DEAL, RECORD_CONTACT } from '../../../models';

/*
READ ME: 

2020-01-27:  We have reached a decision to make the chat related to the contact, therefore minimum adjustments
were made to adapt from relating to Deal ID to Contact ID.  If code seems hacking, please keep this in mind.

1.  All customers (id) are directly related to Deal ID (dl_ID), thus you can have multiple customers appear within the 
customer list that are related to the same contact thus sharing the same chat history.
2.  All customers now have a chat_related_ID property which is strictly related to SNChat.related_ID (co_ID).  Thus,
any future usage of chat by another client should have chat_related_ID set accordingly.  The idea is to show different
deal detail info but share the same chat history as deals can relate to the same contact.
3.  Try to keep all chatItems updated so all status can be derived.  This include a) updated chat through regular data pull,
    b) manual updates when sending a message, c) return chat data on creation
4.  When adding message to message history, add to all customers with the same chat_related_ID.
5.  

*/
export class UscChatAdapter extends ChatAdapter
{
    uscCenteredWindowStyle: any = {
        top: "83px",
        height: "fit-content"
    }

    cmShowSmsTemplate: ChatMenu = {
        icon: "fa fa-comments fa-1",
        Id: "cmShowSmsTemplate",
        title: "Message Templates",
        style: { color: "#41cf61", "vertical-align": "middle", "font-size": "20px", "padding-right": "15px" },
        window: null
    }

    cmShowLanguagePicker: ChatMenu = {
        icon: "fa fa-globe fa-1",
        Id: "cmShowLanguagePicker",
        title: "Change Language Preference",
        style: { color: "#054795", "vertical-align": "middle", "font-size": "20px", "padding-right": "15px" },
        window: null
    }

    uscDetailStyle: any = {
        width: "500px",
        height: "100%",
        float: "left",
        "border-right": "2px solid lightgrey",
    } 

    uscChatPanelStyle: any = {
        width: "500px"
    }

    uscTextareaStyle: any = {
        height: "80px",
        width: "calc(100% - 2px)"
    }

    uscSendMessage: ChatMenu = {
        icon: "fa fa-send fa-1",
        Id: "uscSendMessage",
        title: "Send",
        style: { color: "#054795", "padding": "0px 0px 0px 5px" },
        window: null
    }

    uscToggle: any = {
        label: "new messages only",
        style: {
            float: "right",
            margin: "4px 10px 0px 0px"
        },
        onStyle: { color: "#41cf61", "font-size": "20px", "vertical-align": "-3px" },
        offStyle: { color: "#e6e6e6", "font-size": "20px", "vertical-align": "-3px" }
    }

    allCustomers:User[] = [];
    activeCustomers:User[] = [];            // all customers remains in the customer list for the current session to maintain accessibility and ease of maintenance, unless removed by user
    conversationHistories:any[] = [];
    chatItems:any[] = [];
    currentOpenedWindows:Window[] = [];
    localChatItems:any[] = [];
    localRelatedDeals:any[] = [];
    currentDetailFactory:ComponentFactory<any>;
    chatRelatedDeals:any[] = [];
    unsavedChatItems:any[] = [];
    play_alert_sound: boolean = true;

    private newChatItemSubject = new ReplaySubject<any>();
    public newChatItem = this.newChatItemSubject.asObservable();
    private chatMenuClickedSubject = new ReplaySubject<ChatMenu>();
    public chatMenuClicked = this.chatMenuClickedSubject.asObservable();
    private markRelatedChatAsReadSubject = new ReplaySubject<any>();
    public markRelatedChatAsRead = this.markRelatedChatAsReadSubject.asObservable();

    constructor() {
        super();
        this.maxChatWindow = 1;
        this.inputBarMenu = this.cmShowSmsTemplate;
        this.showDetailPanel = true;
        this.centerChatWindow = true;
        this.showUserAvatar = false;
        this.chatWindowPixelWidth = 1000;
        this.chatWindowDimension =  { width: 1000, height: 610 }
        this.allowChatWindowCollapsing = false;
        this.toggle = this.uscToggle;
        this.reload = this.ngReload;
        this.msgDisabledWarning = "Messaging Disabled: Customer Not Opted-In";

        this.styleSet = {
            centeredWindowStyle: this.uscCenteredWindowStyle,
            chatBodyStyle: this.defaultChatBodyStyle,
            chatPanelStyle: this.uscChatPanelStyle,
            textareaStyle: this.uscTextareaStyle,
            detailStyle: this.uscDetailStyle,
        }

        this.chatMenuItems = [ this.cmShowSmsTemplate, this.cmShowLanguagePicker ];
        //if (this.uscStoreService.isMultiLanguageEnabled()) this.chatMenuItems.push(this.cmShowLanguagePicker);
    }

    loadChatDetailPanel(detailPanelResolver:ComponentFactoryResolver, panelContainer:any, window: Window) {
        if (panelContainer != null) {
            panelContainer.clear();
            this.currentDetailFactory = detailPanelResolver.resolveComponentFactory(UscChatDetailsComponent);
            let ref: ComponentRef<UscChatDetailsComponent> = panelContainer.createComponent(this.currentDetailFactory);
            var item = this.chatRelatedDeals.find(i => i.dl_ID == window.chattingTo.id);
            if (!item) item = this.localChatItems.find(i => i.dl_ID == window.chattingTo.id);
            ref.instance.item = item;
        }
    }

    private GetCustomerByChat(chat, bChatExist):User {
        var customer = this.allCustomers.find(c => c.id == chat.dl_ID);
        if (!customer || !bChatExist) {
            customer = this.convertChatToCustomer(chat);
        } 
        return customer;
    }

    private convertChatToCustomer(chat):User {
        var displayName = this.getDisplayName(chat);
        var bUnreadChat = chat.i_status != 'Read';
        let customer: User = {
            id: chat.dl_ID,
            chat_related_ID: chat.co_ID,
            chat_related_type: RECORD_CONTACT,
            displayName: displayName,
            avatar: "",
            status: null,
            lastMessage: null
        };

        if (chat.cfd_css == 'Opted-In' || (chat.co_type.indexOf('Existing') >= 0 && chat.cfd_esi == "Yes" && chat.oi_status != "OO")) {
            switch (chat.cfd_css) {
                case "Opted-Out": customer.status = UserStatus.OptedOut; break;
                case "Opt-In Requested": customer.status = UserStatus.OptRequested; break;
                case "Opted-In": customer.status =  bUnreadChat ? UserStatus.NotReplied : UserStatus.Replied; break;
                default: customer.status = UserStatus.None; break;
            }
        } else {
            switch (chat.oi_status) {
                case "OO": customer.status = UserStatus.OptedOut; break;
                case "RI": customer.status = UserStatus.OptRequested; break;
                case "OI": customer.status =  bUnreadChat ? UserStatus.NotReplied : UserStatus.Replied; break;
                default: customer.status = UserStatus.None; break;
            }
        }

        return customer;
    }

    private inbound_style = { color: "#15549c", "font-weight": "bold", "font-size": "11px" };
    private outbound_style = { color: "#e07b37", "font-weight": "bold", "font-size": "11px" };
    private convertChatToMessage(chat):Message {
        // inbound msg
        var fromId = chat.dl_ID;
        var toId = chat.dl_user_ID;
        var from = chat.co_firstname + ' ' + chat.co_lastname;
        var from_style = this.inbound_style;
        var inbound = true;
        // outbound msg
        if (chat.ct_user_recipients == '') {
            fromId = chat.dl_user_ID;
            from_style = this.outbound_style;
            toId = chat.dl_ID;
            from = chat.created_by_firstname + ' ' + chat.created_by_lastname;
            inbound = false;
        }

        let message:Message = {
            from: from,
            from_style: from_style,
            fromId: fromId,
            toId: toId,
            inbound: inbound,
            message: chat.cfd_dm ? chat.cfd_dm : chat.ct_message,
            seenOn: chat.i_datetime_modified ? new Date(chat.i_datetime_modified) : null,
            createdOn: chat.ct_datetime_created ? new Date(chat.ct_datetime_created) : null,
        }
        return message;
    }

    private isActiveCustomer(customer:User):boolean {
        return this.isCustomer(this.activeCustomers, customer);
    }

    private isExistingCustomer(customer:User): boolean {
        return this.isCustomer(this.allCustomers, customer);
    }

    private isCustomer(customers:User[], customer:User): boolean {
        var isCustomer = false;
        for (var i = 0; i < customers.length; i++) {
            if (customers[i].id == customer.id) {
                isCustomer = true;
                break;
            }
        }
        return isCustomer;
    }

    private isChatExists(chat):boolean {
        if (this.chatItems) {
            var chatFound  = this.chatItems.find(c => c.ct_ID == chat.ct_ID);
        } 
        return chatFound ? true : false;
    }

    private isUnsavedChat(chat): boolean {
        if (this.unsavedChatItems) {
            var chatFound = this.unsavedChatItems.find(c => c.dl_ID == chat.dl_ID && c.cfd_uid == chat.cfd_uid);
        }
        return chatFound ? true : false;
    }

    toggleClicked() {
        if (this.toggle) {
            this.toggle.selected = !this.toggle.selected;
            this.updateCustomerList();
        }
    }

    reloadClicked() {
        this.uscStoreService.setTriggerChatItemReload();
    }

    // evaluate and update all customer status and add to active customers if required
    // this evaluation should not remove customer from the active customers list 
    private updateAllCustomersStatus() {
        this.allCustomers.forEach(c => {
            var ooChat = this.chatItems.find(i => i.co_ID == c.chat_related_ID && i.dl_stage != 'Archive' && i.oi_status == "OO");     // opt out
            var riChat = this.chatItems.find(i => i.co_ID == c.chat_related_ID && i.dl_stage != 'Archive' && i.oi_status == "RI");     // opt requested
            var oiChat = this.chatItems.find(i => i.co_ID == c.chat_related_ID && i.dl_stage != 'Archive' && i.oi_status == "OI");     // opt in

            var cm_ooChat = this.chatItems.find(i => i.dl_ID == c.id && i.dl_stage != 'Archive' && i.oi_status != "OO" && i.co_type.indexOf('Existing') >= 0 && i.cfd_esi == "Yes" && i.cfd_css == "Opted-Out");           // campaign opt out
            var cm_riChat = this.chatItems.find(i => i.dl_ID == c.id && i.dl_stage != 'Archive' && i.oi_status != "OO" && i.co_type.indexOf('Existing') >= 0 && i.cfd_esi == "Yes" && i.cfd_css == "Opt-In Requested");    // campaign opt requested
            var cm_oiChat = this.chatItems.find(i => i.dl_ID == c.id && i.dl_stage != 'Archive' && i.cfd_css == "Opted-In");            // campaign opt in

            // analyze 1) campaign opt-in status first then 2) overall opt-in and set UserStatus
            if (cm_ooChat) c.status = UserStatus.OptedOut;
            else if (cm_riChat) c.status = UserStatus.OptRequested;
            else if (cm_oiChat) {
                var chat = this.chatItems.find(i => i.co_ID == c.chat_related_ID && i.i_status != "Read");
                c.status = (chat) ? UserStatus.NotReplied : UserStatus.Replied;
            } else if (ooChat) c.status = UserStatus.OptedOut;
            else if (riChat) c.status = UserStatus.OptRequested;
            else if (oiChat) {
                var chat = this.chatItems.find(i => i.co_ID == c.chat_related_ID && i.i_status != "Read");
                c.status = (chat) ? UserStatus.NotReplied : UserStatus.Replied;
            } else {
                c.status = UserStatus.None;
            }

            if (this.conversationHistories[c.chat_related_ID] && this.conversationHistories[c.chat_related_ID].length > 0) {
                c.lastMessage = this.conversationHistories[c.chat_related_ID][this.conversationHistories[c.chat_related_ID].length - 1];
            }

            var aIndex = this.activeCustomers.findIndex(a => a.id == c.id);
            if (aIndex >= 0) {
                this.activeCustomers[aIndex] = c;
            } else if (c.status == UserStatus.NotReplied) {
                this.activeCustomers.push(c);
            }
        })
    }

    private updateCustomerList() {
        this.updateAllCustomersStatus();
        if (this.toggle.selected) {
            let notRepliedCustomers:User[] = [];
            this.activeCustomers.forEach(c => {
                if (c.status == UserStatus.NotReplied) {
                    notRepliedCustomers.push(c);
                }
            });
            //console.log({event: "updateCustomerList", nrc: notRepliedCustomers});
            this.onFriendsListChanged(this.getSortedCustomerList(notRepliedCustomers));
        } else {
            //console.log({event: "updateCustomerList", ac: this.activeCustomers});
            this.onFriendsListChanged(this.getSortedCustomerList(this.activeCustomers));
        }
    }

    getUserAlertCount() {
        var count = 0;
        this.activeCustomers.forEach(c => {
            if (c.status == UserStatus.NotReplied) {
                count++;
            }
        });
        return count;
    }

    // Called by the parent component to provide all chat related items 
    public setChatRelatedItems(updatedDeals) {
        // first time initializing, so just set to updatedDeals
        if (!this.chatRelatedDeals || this.chatRelatedDeals.length == 0) {
            this.chatRelatedDeals = updatedDeals;
        } else {
            // loop through each deal in updatedDeals, for each existing deal, update the related customer
            updatedDeals.forEach(d => {
                var existingDeal = this.chatRelatedDeals.find(i => i.dl_ID == d.dl_ID);
                if (existingDeal) {
                    var index = this.chatRelatedDeals.indexOf(existingDeal);
                    this.chatRelatedDeals[index] = d;
                    // has unread msg
                    if (existingDeal.cfd_hum == "Yes") {
                        var customer = this.activeCustomers.find(c => c.id == existingDeal.dl_ID);
                        if (customer) {
                            customer.status = UserStatus.NotReplied;
                            this.updateCustomer(customer);
                        }
                    }
                    // if deal is archived or won
                    if (existingDeal.dl_stage == 'Archive' || existingDeal.dl_status == 'WO') {
                        var i = this.activeCustomers.indexOf(customer);
                        this.activeCustomers.splice(i, 1);
                    }
                    // check 1) overall optin status    2) campaign optin enabled   3) campaign optin status
                    if (existingDeal.oi_status != d.oi_status || existingDeal.cfd_esi != d.cfd_esi || existingDeal.cfd_css != d.cfd_css || existingDeal.co_type != d.co_type) {
                        var customer = this.activeCustomers.find(c => c.id == existingDeal.dl_ID);
                        if (customer) {
                            // campaign optin status
                            if (d.cfd_css == 'Opted-In' || (d.co_type.indexOf('Existing') >= 0 && d.cfd_esi == "Yes" && d.oi_status != "OO")) {
                                switch (d.cfd_css) {
                                    case "Opted-Out": customer.status = UserStatus.OptedOut; break;
                                    case "Opt-In Requested": customer.status = UserStatus.OptRequested; break;
                                }
                            } else {
                                // overall optin status
                                switch (d.oi_status) {
                                    case "OO": customer.status = UserStatus.OptedOut; break;
                                    case "RI": customer.status = UserStatus.OptRequested; break;
                                }
                            }
                        }
                    }
                } else {
                    this.chatRelatedDeals.push(d);
                }
            })
        }
    }

    public updateRelatedDeal(updated_deal) {
        //console.log("updateRelatedDeal");
        var target_dl_ID = updated_deal.dl_ID;
        var deal = this.chatRelatedDeals.find(d => d.dl_ID == updated_deal.dl_ID);
        if (deal) {
            var index = this.chatRelatedDeals.indexOf(deal);
            this.chatRelatedDeals[index] = updated_deal;
        } else {
            this.chatRelatedDeals.push(updated_deal);
        }

        // when winning a middle impression, a new lasting impression will be created, 
        // thus we need to associate to the new lasting impression without clearing the chat history (new lead no chats)
        // so i decided just update the displayName and the dl_stage (lasting impression may be Archive)
        var bMiddleToLasting = false;
        if (updated_deal.from_dl_ID && updated_deal.from_dl_ID != target_dl_ID) {
            bMiddleToLasting = true;
            target_dl_ID = updated_deal.from_dl_ID;
            if (deal && deal.dl_stage != updated_deal.stage) deal.dl_stage = updated_deal.stage;
        }

        var existingCustomer = this.allCustomers.find(c => c.id == target_dl_ID);
        if (existingCustomer) existingCustomer.displayName = this.getDisplayName(updated_deal);

        var customer = this.activeCustomers.find(c => c.id == target_dl_ID);
        if (customer) {
            // if deal is archived, won, or become another deal then remove the existing customer from activeCustomers list
            if (updated_deal.dl_stage == 'Archive' || updated_deal.dl_status == 'WO' || bMiddleToLasting) {
                var i = this.activeCustomers.indexOf(customer);
                this.activeCustomers.splice(i, 1);
                i = this.allCustomers.findIndex(c => c.id == target_dl_ID);
                this.allCustomers.splice(i, 1);
            } 
            if (this.currentOpenedWindows.length > 0) this.currentOpenedWindows[0].chattingTo = customer;
            this.updateCustomerList();
        }
    }

    public resetCustomerChats() {
        this.chatItems = [];
        this.chatRelatedDeals = [];
        this.allCustomers = [];
        this.activeCustomers = [];
        this.conversationHistories = [];
        this.chatItems = [];
        this.currentOpenedWindows = [];
        this.localChatItems = [];
        this.localRelatedDeals = [];
        this.chatRelatedDeals = [];
        this.unsavedChatItems = [];   
        this.onFriendsListChanged([]);
    }

    // chat messages are sorted by oldest to newest
    public setCustomerChats(data) {
        var bInitialChatPull = this.chatItems.length == 0;
        if (data && data.items) {
            var bCustomerListUpdated = false;
            for (var i = 0; i < data.items.length; i++) {
                var chat = data.items[i];
                var bChatExist = this.isChatExists(chat);
                var bUnsavedChat = this.isUnsavedChat(chat);
                // existing chat, update and check relevant status
                if (bChatExist && !bUnsavedChat) {
                    var existingChat = this.chatItems.find(c => c.ct_ID == chat.ct_ID);
                    if (existingChat.dl_ID == chat.dl_ID) {
                        this.chatItems[this.chatItems.indexOf(existingChat)] = chat;
                    }
                    // always update to the new status
                    if (existingChat.i_status != chat.i_status && existingChat.i_status != 'Read') {
                        bCustomerListUpdated = true;
                    }
                    // overall opt-in status
                    if (existingChat.oi_status != chat.oi_status) {
                        bCustomerListUpdated = true;
                    }
                    // campaign opt-in status
                    if (existingChat.cfd_esi != chat.cfd_esi || existingChat.cfd_css != chat.cfd_css || existingChat.co_type != chat.co_type) {
                        bCustomerListUpdated = true;
                    }

                    // this could be another lead related to the same contact, add that lead as a customer
                    if (existingChat.dl_ID != chat.dl_ID) {
                        this.addCustomerUser(this.GetCustomerByChat(chat, bChatExist));
                        bCustomerListUpdated = true;
                    }
                } 
                // brand new chat, add to collection and update all other controls
                else if (!bUnsavedChat) {     
                    bCustomerListUpdated = true;
                    var c = this.GetCustomerByChat(chat, bChatExist);
                    let message:Message = this.convertChatToMessage(chat);
                    this.chatItems.push(chat);
                    this.addCustomerUser(c);
                    this.addMessageToConversation(c.chat_related_ID, message);
                    this.updateChatWindowWithMessage(chat, message);
                    if (!bInitialChatPull && this.uscStoreService && this.play_alert_sound) {
                        //this.play_alert_sound = false;
                        this.uscStoreService.setTitle("U.S. Cellular - New Message", true);
                        setTimeout(() => {
                            this.play_alert_sound = true;
                            this.uscStoreService.setTitle("U.S. Cellular - Making Sales Happen");
                        }, 1 * 60 * 1000);
                    } 
                } 
                // recently created outbound chat by this session, lets update the unsavedChat with the actual one with full data
                else if (bUnsavedChat) {
                    var existingChat = this.chatItems.find(c => c.ct_ID == chat.ct_ID);
                    this.chatItems[this.chatItems.indexOf(existingChat)] = chat;
                }
            }

            // update customer list with proper status
            if (bCustomerListUpdated) {
                this.updateCustomerList();
            } 
        }
    }

    public addNewChatItem(chatItem) {
        if (!this.isChatExists(chatItem)) {
            this.chatItems.push(chatItem);
        } 
    }

    public getChatItemsByWindow(window:Window): any[] {
        var items = [];
        this.chatItems.forEach(c => {
            if (c.dl_ID == window.chattingTo.id) {
                items.push(c);
            }
        });
        if (items.length == 0) {
            this.localChatItems.forEach(c => {
                if (c.dl_ID == window.chattingTo.id) {
                    items.push(c);
                }
            });
        }
        return items;
    }

    public getRelatedDealByChatItem(chatItem) {
        var deal = this.chatRelatedDeals.find(d => d.dl_ID == chatItem.dl_ID);
        if (!deal) deal = this.localRelatedDeals.find(d => d.dl_ID == chatItem.dl_ID);
        return deal;
    }

    public addCustomerUser(new_customer:User, bAddToListOverride = false): any[] {
        var bActiveCustomerAdded = false;
        var current_customer = null;
        if (new_customer.id && new_customer.id > 0) {
            if (!this.isExistingCustomer(new_customer)) {
                this.allCustomers.push(new_customer);
                current_customer = new_customer;
            } else {
                current_customer = this.allCustomers.find(c => c.id == new_customer.id);
                if (current_customer.status != new_customer.status) {
                    current_customer.status = new_customer.status;
                }
            }

            if (current_customer.status == UserStatus.NotReplied || bAddToListOverride) {
                if (!this.isActiveCustomer(new_customer)) {
                    this.activeCustomers.push(new_customer);
                    bActiveCustomerAdded = true;
                }
            }
        }
        return [current_customer, bActiveCustomerAdded];
    }

    public addMessageToConversation(chat_related_ID:number, message:Message) {
        if (this.conversationHistories[chat_related_ID]) {
            this.conversationHistories[chat_related_ID].push(message);
        } else {
            this.conversationHistories[chat_related_ID] = [ message ];
        }
        //console.log(message);
    }

    private getDisplayName(item): string {
        var displayName = '_FN_ _LN_ (_CM_) - _IM_'.replace('_FN_', item.co_firstname).replace('_LN_', item.co_lastname).replace('_CM_', item.cm_name).replace('_IM_', item.dl_type);
        return displayName;
    }

    openUserChatWindow(item: any): void { 
        var bChatFound = false;
        for (var i = 0; i < this.chatItems.length; i++) {
            var chat = this.chatItems[i];
            if (chat.dl_user_ID == item.u_user_ID && chat.co_ID == item.co_ID && chat.dl_ID == item.dl_ID) {
                let customer: User = this.convertChatToCustomer(chat);
                if (this.isActiveCustomer(customer)) {
                    this.onOpenUserChatWindow(customer);
                    bChatFound = true;
                } else if (this.isExistingCustomer(customer)) {
                    bChatFound = true;
                    customer.lastMessage = this.conversationHistories[customer.chat_related_ID][this.conversationHistories[customer.chat_related_ID].length - 1];
                    var result = this.addCustomerUser(customer, true);
                    this.onOpenUserChatWindow(customer);
                } 
            }
        }

        if (!bChatFound) {
            var displayName = this.getDisplayName(item);
            let customer: User = {
                id: item.dl_ID,
                chat_related_ID: item.co_ID,
                chat_related_type: RECORD_CONTACT,
                displayName: displayName,
                avatar: "",
                status: UserStatus.Replied,
                lastMessage: null
            }
            
            if (this.conversationHistories[customer.chat_related_ID] && this.conversationHistories[customer.chat_related_ID].length > 0) {
                customer.lastMessage = this.conversationHistories[customer.chat_related_ID][this.conversationHistories[customer.chat_related_ID].length - 1];
            }

            // campaign lead opt-in status
            if (item.cfd_css == 'Opted-In' || (item.cfd_esi == "Yes" && item.oi_status != "OO" && item.co_type.indexOf('Existing') >= 0)) {
                switch (item.cfd_css) {
                    case "Opted-Out": customer.status = UserStatus.OptedOut; break;
                    case "Opt-In Requested": customer.status = UserStatus.OptRequested; break;
                }
            } else {
                // overall opt-in status
                switch (item.oi_status) {
                    case "OO": customer.status = UserStatus.OptedOut; break;
                    case "RI": customer.status = UserStatus.OptRequested; break;
                }
            }

            this.updateCustomer(customer);
            this.addCustomerUser(customer, true);
            this.onOpenUserChatWindow(customer);

            item.ct_related_type = RECORD_DEAL;
            item.ct_message = "";
            item.ct_user_recipients = "";
            item.ct_team_recipients = "";
            item.ct_recipient_details = "";
            item.i_status = "";
            this.localChatItems.push(item);
            this.localRelatedDeals.push(item);
        }
    }

    userChatWindowOpened(window: Window): void { 
        this.currentOpenedWindows.push(window);
    }

    userChatWindowClosed(window: Window): void { 
        let index = this.currentOpenedWindows.indexOf(window);
        this.currentOpenedWindows.splice(index, 1);
    }

    onUserRmoved(customer: User): void { 
        var item = this.chatRelatedDeals.find(i => i.dl_ID == customer.id);
        this.markRelatedChatAsReadSubject.next(item);
        var c = this.allCustomers.find(c => c.id == customer.id);
        if (c.status == UserStatus.NotReplied) c.status = UserStatus.Replied;
        var index = this.activeCustomers.findIndex(c => c.id == customer.id);
        if (index >= 0) this.activeCustomers.splice(index, 1);
        this.chatItems.forEach(i => {
            if (i.co_ID == customer.chat_related_ID && i.i_status != 'Read') {
                i.i_status = 'Read'
            }
        })
        this.updateCustomerList();
    }
    
    updateChatWindowWithMessage(chat, message) {
        this.currentOpenedWindows.forEach(w => {
            if (w.chattingTo.chat_related_ID == chat.co_ID) {
                this.onMessageReceived(w.chattingTo, message);
            }
        })
    }

    isUserChatWindowOpened(chat, bExpandOnCollapsed = true): boolean {
        var window = this.currentOpenedWindows.find(w => w.chattingTo.id == chat.dl_ID);
        if (window) {
            window.isCollapsed = false; 
            return true; 
        }
        return false;
    }

    onMenuClicked(menu: ChatMenu) {
        this.chatMenuClickedSubject.next(menu);
    }

    listFriends(): Observable<User[]> {
        return Observable.of(this.activeCustomers);
    }

    getMessageHistory(user: User): Observable<Message[]> {
        let conversation: Array<Message> = this.conversationHistories[user.chat_related_ID];

        return Observable.of(conversation);
    }
    
    sendMessage(message: Message): Message {
        // console.log("sending message");
        // console.log(message);
        
        var uid = (new Date()).getTime();
        var customer = this.allCustomers.find(x => x.id == message.toId);
        this.addMessageToConversation(customer.chat_related_ID, message);
        customer.status = UserStatus.Replied;
        customer.lastMessage = message;
        this.updateCustomer(customer);
        // update the message from with the current user displayName
        if (this.current_sender) message.from = this.current_sender.displayName;
        message.from_style = this.outbound_style;
        message.toId = customer.chat_related_ID;

        var deal = this.chatRelatedDeals.find(d => d.dl_ID == customer.id);
        if (!deal) deal = this.localRelatedDeals.find(d => d.dl_ID == customer.id);

        this.unsavedChatItems.push({
            dl_ID: deal.dl_ID,
            dl_name: deal.dl_name,
            dl_user_ID: deal.dl_user_ID,              
            dl_type: deal.dl_type,
            co_ID: deal.co_ID,
            co_firstname: deal.co_firstname,
            co_lastname: deal.co_lastname,
            co_work_phone1: deal.co_work_phone1,
            co_type: deal.co_type,
            cm_name: deal.cm_name,
            cfd_uid: uid,
            ct_ID: 0,
            ct_chat_ID: 0,
            ct_parent_ID: 0,
            ct_related_ID: deal.ID,
            ct_related_type: RECORD_DEAL,
            ct_message: message.message
        });

        var chatFormItem = [
            { sn_ID: "CT-ID",			    key: "ct_ID",			    data_type: "N",     value: 0	                        },
            { sn_ID: "CT-related_ID",	    key: "ct_related_ID",	    data_type: "I",	    value: customer.chat_related_ID	    },
            { sn_ID: "CT-related_type",	    key: "ct_related_type",	    data_type: "T",	    value: customer.chat_related_type   },
            { sn_ID: "CT-message",	        key: "ct_message",	        data_type: "T",	    value: message.message              },
            { sn_ID: "CT-send_email",	    key: "ct_send_email",	    data_type: "T",	    value: "N"                          },
            { sn_ID: "DL-ID",	            key: "dl_ID",	            data_type: "N",	    value:  deal.dl_ID                  },
        ];

        // HACK: hardcode the custom field id for Chat.uid field depending on the client
        if (this.isUSCellCorp()) {
            chatFormItem.push({ sn_ID: "CT-59866",	    key: "cfd_uid",	        data_type: "T",	    value: uid              });
        } else if (this.isUSCellAgent()) {
            chatFormItem.push({ sn_ID: "CT-60051",	    key: "cfd_uid",	        data_type: "T",	    value: uid              });
        } else if (this.isUSCellInsideSales()) {
            chatFormItem.push({ sn_ID: "CT-64670",	    key: "cfd_uid",	        data_type: "T",	    value: uid              });
        } else if (this.isUSCellTelesales()) {
            chatFormItem.push({ sn_ID: "CT-70301",	    key: "cfd_uid",	        data_type: "T",	    value: uid              });
        }

        this.activeCustomers.forEach(a => {
            if (a.chat_related_ID == customer.chat_related_ID && a.id != customer.id) {
                a.status = UserStatus.Replied;
                a.lastMessage = message;
            }
        })

        this.chatItems.forEach(c =>  {
            if (c.co_ID == customer.chat_related_ID && c.i_status != 'Read') {
                c.i_status = 'Read';
            }
        })

        this.newChatItemSubject.next(chatFormItem);

        return message;
    }

    private updateCustomer(customer:User, bUpdateCustomerList = true) {
        var cIndex = this.allCustomers.indexOf(customer);
        if (cIndex >= 0) this.allCustomers[cIndex] = customer;
        var activeCustomer = this.activeCustomers.find(c => c.id == customer.id);
        var aIndex = this.activeCustomers.indexOf(activeCustomer);
        if (aIndex >= 0) {
            var bUpdateNeeded = false;
            if (customer.status && activeCustomer.status  != customer.status) {
                activeCustomer.status = customer.status;
                bUpdateNeeded = true;
            }
            if (customer.lastMessage && activeCustomer.lastMessage != customer.lastMessage)  {
                activeCustomer.lastMessage = customer.lastMessage;
                bUpdateNeeded = true;
            }
            
            //this.activeCustomers[aIndex] = activeCustomer;
            if (bUpdateNeeded && bUpdateCustomerList) {
                console.log("active customer updated");
                this.updateCustomerList();
            }
        } 
    }

    getSortedCustomerList(customers: User[], sortDetail = null): User[] {
        var sorted = [];
        return customers.sort(function(a, b) {
            var bMsgTime = b.lastMessage ? b.lastMessage.createdOn.getTime() : 0;
            var aMsgTime = a.lastMessage ? a.lastMessage.createdOn.getTime() : 0;
            return bMsgTime - aMsgTime;
        });
    }

    public uscStoreService: any;
    isUSCellCorp(): boolean {
        return (this.uscStoreService && this.uscStoreService.isUSCellCorp());
    }
    
    isUSCellAgent(): boolean {
        return (this.uscStoreService && this.uscStoreService.isUSCellAgent());
    }

    isUSCellInsideSales(): boolean {
        return (this.uscStoreService && this.uscStoreService.isUSCellInsideSales());
    }

    isUSCellTelesales(): boolean {
        return (this.uscStoreService && this.uscStoreService.isUSCellTelesales());
    }

    validateMessage(message: string): boolean {
        var bValid = true;
        if (this.isCensorshipEnabled()) {
            bValid = this.uscStoreService.isSmsSafe(message);
        }
        return bValid;
    }

    enableLanguagePicker(bEnable: boolean) {
        if (bEnable) {
            this.chatMenuItems = [ this.cmShowSmsTemplate, this.cmShowLanguagePicker ];
        } else {
            this.chatMenuItems = [ this.cmShowSmsTemplate ];
        }
    }

    isCensorshipEnabled(): boolean {
        return (this.uscStoreService && this.uscStoreService.isCensorshipEnabled()); 
    }

    getNoMessageWarning(): string { return "Remember: Under no circumstances should you attempt to verify a customer through SMS. Do not ask for a customers pin number over SMS."; }
}


// WEBPACK FOOTER //
// C:/Azure/Source/Workspaces/SalesNOW/SalesSOLO/WebSites/SalesSOLO.Web.Angular.telesales/src/app/components/usc-store/usc-chat-adapter/usc-chat-adapter.ts